import {
  brandOptions,
  organizations,
  suppliers,
  contactPersons,
  adminUnits,
} from './itemsFixtureData';
import { mandatoryExportCustomFieldDefinition } from './bulkUpdateProbeFixtureData';

/**
 * Deterministic item probes for epic DL-66347 (D2 Playwright automation — dl-58912 "bulk edit
 * validation skips untouched fields but still fires on touched ones", Testmo folder
 * https://delogue.testmo.net/repositories/4?group_id=101500). Isolable via `?search=Dl58912Probe`
 * (unique substring, absent from every other fixture name), with a sub-word per scenario for
 * further narrowing (e.g. `?search=Dl58912Probe Partial`).
 *
 * Reuses `mandatoryExportCustomFieldDefinition` (customFieldId 90, "Export Note") from the
 * sibling DL-62328 epic's `bulkUpdateProbeFixtureData.ts` — same mandatory-for-export CF, so one
 * `apiTransform` on `/api/meta/items/customfields` serves both epics' specs. This epic's positive
 * spine (dl-66349/888818), explicit-clear guard (dl-66353/888820), touched-CF-together positive
 * (dl-66359/888823) and cancel regression (dl-66368/888834) reuse that sibling epic's own
 * `bulkUpdateProbeExportReadyA`/`B` fixtures directly rather than duplicating them here — see
 * this epic's specs for why.
 */

type TextCustomFieldValue = {
  id: number;
  customFieldId: number;
  name: string;
  userDefinedId: string | null;
  type: number;
  value: string;
  itemCustomFieldAllowedValueId: number | null;
  customFieldAllowedValueUserDefinedID: string | null;
  isMandatory: boolean;
  isSupplierEditable: boolean;
  internalOnly: boolean;
  maxChar: number | null;
  isMultiLine: boolean;
  allowedDecimals: number;
  isMultipleAllowedValue: boolean;
  parentCustomFieldId: number | null;
  parentCustomFieldName: string | null;
  allowedValues: unknown[];
};

const exportNoteCf = (cfEntryId: number, value: string): TextCustomFieldValue => ({
  id: cfEntryId,
  customFieldId: mandatoryExportCustomFieldDefinition.customFieldId,
  name: mandatoryExportCustomFieldDefinition.name,
  userDefinedId: mandatoryExportCustomFieldDefinition.userDefinedId,
  type: mandatoryExportCustomFieldDefinition.type,
  value,
  itemCustomFieldAllowedValueId: null,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: mandatoryExportCustomFieldDefinition.isMandatory,
  isSupplierEditable: mandatoryExportCustomFieldDefinition.isSupplierEditable,
  internalOnly: mandatoryExportCustomFieldDefinition.internalOnly,
  maxChar: mandatoryExportCustomFieldDefinition.maxChar,
  isMultiLine: mandatoryExportCustomFieldDefinition.isMultiLine,
  allowedDecimals: mandatoryExportCustomFieldDefinition.allowedDecimals,
  isMultipleAllowedValue: mandatoryExportCustomFieldDefinition.isMultipleAllowedValue,
  parentCustomFieldId: mandatoryExportCustomFieldDefinition.parentCustomFieldId,
  parentCustomFieldName: mandatoryExportCustomFieldDefinition.parentCustomFieldName,
  allowedValues: [],
});

const dl58912ProbeBase = {
  organization: organizations[0],
  supplier: suppliers[0],
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  logo: null,
  suppliedBy: null,
  updatedAfterLastExport: false,
  lastUpdated: '2026-01-15T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
};

/** dl-66357 (Testmo #888822) — neither item is Ready for Export; the mandatory CF is left with
 *  NO value at all (not merely unfilled-but-present) so the RFE=No exemption is genuinely
 *  exercised rather than passing because the CF happened to already be filled. */
export const dl58912ProbeNoRfeA = {
  ...dl58912ProbeBase,
  id: 95801,
  name: 'Dl58912Probe NoRfe A',
  userDefinedId: 'IT-95801',
  description: null,
  brand: brandOptions[0],
  adminUnit: adminUnits[0],
  isActive: true,
  defaultQty: 1,
  readyForExport: false,
  customFields: [] as TextCustomFieldValue[],
};
export const dl58912ProbeNoRfeB = {
  ...dl58912ProbeNoRfeA,
  id: 95802,
  name: 'Dl58912Probe NoRfe B',
  userDefinedId: 'IT-95802',
};

/** dl-66361 (Testmo #888826) — a genuinely MIXED selection: one item Ready for Export, one not,
 *  in the SAME edit — the failure mode neither an all-Yes nor an all-No selection would catch. */
export const dl58912ProbeMixedRfeYes = {
  ...dl58912ProbeBase,
  id: 95803,
  name: 'Dl58912Probe Mixed RFE Yes',
  userDefinedId: 'IT-95803',
  description: null,
  brand: brandOptions[0],
  adminUnit: adminUnits[0],
  isActive: true,
  defaultQty: 1,
  readyForExport: true,
  customFields: [exportNoteCf(95803090, 'Mixed yes note')],
};
export const dl58912ProbeMixedRfeNo = {
  ...dl58912ProbeMixedRfeYes,
  id: 95804,
  name: 'Dl58912Probe Mixed RFE No',
  userDefinedId: 'IT-95804',
  readyForExport: false,
  customFields: [] as TextCustomFieldValue[],
};

/** dl-66369 (Testmo #888835) — the sharpest case in the folder: Item A's mandatory CF is FILLED,
 *  Item B's is genuinely EMPTY, both Ready for Export. Left at Keep Current, the save must still
 *  succeed and Item B's CF must remain empty afterwards — proof the validation reads nothing at
 *  all from an untouched field rather than falling back to "any item's" value. */
export const dl58912ProbePartialFillA = {
  ...dl58912ProbeBase,
  id: 95805,
  name: 'Dl58912Probe Partial Fill A',
  userDefinedId: 'IT-95805',
  description: null,
  brand: brandOptions[0],
  adminUnit: adminUnits[0],
  isActive: true,
  defaultQty: 1,
  readyForExport: true,
  customFields: [exportNoteCf(95805090, 'Partial fill A note')],
};
export const dl58912ProbePartialFillB = {
  ...dl58912ProbePartialFillA,
  id: 95806,
  name: 'Dl58912Probe Partial Fill B',
  userDefinedId: 'IT-95806',
  customFields: [] as TextCustomFieldValue[],
};

/** dl-66363 (Testmo #888828) — 20 Ready-for-Export items, mandatory CF filled on every one, for
 *  a large-selection Brand-only edit (functional half only — the case's "no timeout" claim is
 *  not meaningful against MirageJS, see the epic's divergence 5). */
export const dl58912ProbeScaleItems = Array.from({ length: 20 }, (_, i) => ({
  ...dl58912ProbeBase,
  id: 95820 + i,
  name: `Dl58912Probe Scale ${String(i + 1).padStart(2, '0')}`,
  userDefinedId: `IT-${95820 + i}`,
  description: null,
  brand: brandOptions[0],
  adminUnit: adminUnits[0],
  isActive: true,
  defaultQty: 1,
  readyForExport: true,
  customFields: [exportNoteCf(95820090 + i, `Scale note ${i + 1}`)],
}));

export const dl58912ItemsFixtureData = [
  dl58912ProbeNoRfeA,
  dl58912ProbeNoRfeB,
  dl58912ProbeMixedRfeYes,
  dl58912ProbeMixedRfeNo,
  dl58912ProbePartialFillA,
  dl58912ProbePartialFillB,
  ...dl58912ProbeScaleItems,
];
